import { DateParserService } from '../daily-log/daily-log-time/date-parser-service';
import { PtLuxonDate } from '../../models/projects/date.models';
import { HttpClient } from '@angular/common/http';
import { DbContext, DbTable } from '../data/dbcontext.service';
import { DateService } from '../date/date.service';
import { Injectable } from '@angular/core';
import { MorningMinutesEditor } from '../../models/morning-minutes/morning-minutes.models';
import { TimeZoneService } from '../time-zone/time-zone.service';
import { AppConfig } from '../../app/app.config';
import { TimeZoneChangeEditor } from '../../models/time-zones/time-zone.models';

@Injectable()
export class MorningMinutesService {
  config = AppConfig.settings;
  private readonly morningMinutesTable: DbTable;

  constructor(
    private http: HttpClient,
    private dbContext: DbContext,
    private dateService: DateService,
    private dateParserService: DateParserService,
    private timeZoneService: TimeZoneService
  ) {
    this.morningMinutesTable = this.dbContext.tables.find(t => t.name === 'MorningMinutesData');
  }

  deleteAllMorningMinutesRecords() {
    return this.dbContext.removeAll(this.morningMinutesTable);
  }

  async saveEditor(morningMinutesEditor: MorningMinutesEditor) {
    morningMinutesEditor = this.dateParserService.removeOffsetFromLuxonDates(morningMinutesEditor);
    const url = `${this.config.apiUrl}api/morningminutes/save`;
    const result = await this.http.post<any>(url, morningMinutesEditor).toPromise();
    const data = await this.deserializeMorningMinutesTimes(result);
    await this.syncMorningMinutesData(data);
    return data;
  }

  async saveMorningMinutesEditorLocally(editor: MorningMinutesEditor) {
    if (!editor.id && !editor.localId) {
      editor.localId = await this.dbContext.getTempId(this.morningMinutesTable) as number;
    }

    editor = await this.dateParserService.removeOffsetFromLuxonDates(editor);
    await this.dbContext.addOrUpdate(this.morningMinutesTable, editor.id ? editor.id : editor.localId, editor);
  }

  async getMorningMinutesDataForDay(date?: PtLuxonDate): Promise<MorningMinutesEditor> {
    let instances = await this.dbContext.getAll(this.morningMinutesTable) as MorningMinutesEditor[];

    for (let i = 0; i < instances.length; i++) {
      instances[i] = await this.deserializeMorningMinutesTimes(instances[i]);
    }

    const filteredInstances: MorningMinutesEditor[] = [];

    for (let i = 0; i < instances.length; i++) {
      const sameDay = await this.dateService
        .compareDayOffsetsPreApplied(date.dateTime, instances[i].date);

      if (sameDay) {
        filteredInstances.push(instances[i]);
      }
    }

    let editor = filteredInstances[0];

    if (!!editor) {
      return editor;
    }

    editor = new MorningMinutesEditor();
    editor.date = date.dateTime;

    return editor;
  }

  async getAndUpdateMorningMinutesData(date: PtLuxonDate): Promise<MorningMinutesEditor> {
    const updatedData = await this.getUpdatedMorningMinutesData(date);
    await this.syncMorningMinutesData(updatedData);
    return updatedData;
  }

  private async syncMorningMinutesData(morningMinutesData: MorningMinutesEditor) {
    await this.deleteAllMorningMinutesRecords();
    if (morningMinutesData != null) {
      await this.saveMorningMinutesEditorLocally(morningMinutesData);
    }
  }

  public async getUpdatedMorningMinutesData(date: PtLuxonDate): Promise<MorningMinutesEditor> {
    const dateUtc = this.dateParserService.removeOffsetFromLuxonDate(date.dateTime);
    const url = `${this.config.apiUrl}api/morningminutes/getdatafordate?date=${dateUtc.toJSON()}`;
    const result = this.http.get<MorningMinutesEditor>(url).toPromise();
    return await this.dateParserService.deserializeDates(result, true, this.timeZoneService);
  }

  async deserializeMorningMinutesTimes(instance: MorningMinutesEditor): Promise<MorningMinutesEditor> {
    if (instance.date) {
      instance.date = await this.dateParserService.deserializeDate(instance.date, true, this.timeZoneService);
    }

    if (instance.meetingDate) {
      instance.meetingDate = await this.dateParserService.deserializeDate(instance.meetingDate, true, this.timeZoneService);
    }

    return instance;
  }
}



// WEBPACK FOOTER //
// ./src/services/morning-minutes/morning-minutes.service.ts